import { constructUrl } from "@/utils/utils";
import { getType } from "@/api/getType";
import {
  ApiMethod,
  type ApiSchema,
  EXCEL_DIMENSIONALITY,
  type ExcelFunction,
  type ExcelFunctionParameter,
  JSON_CONTENT_TYPE,
  type PathMethodDetails,
} from "@/stores/types/api.types";

class GetMethodConstructor {
  private details: PathMethodDetails;
  private schema: ApiSchema;

  constructor(details: PathMethodDetails, schema: ApiSchema) {
    this.details = details;
    this.schema = schema;
  }

  construct(): ExcelFunctionParameter[] {
    const parameters: ExcelFunctionParameter[] = [];

    if (this.details.parameters) {
      this.details.parameters.forEach((param) => {
        const typeResult = getType({ type: param.schema.type });

        parameters.push({
          description: param.description,
          name: param.name,
          optional: !param.required,
          default: param.schema.default,
          type: typeResult.type,
          schemaType: typeResult.schemaType,
          nestLevel: typeResult.nestLevel,
        });
      });
    }

    return parameters;
  }
}

class PostMethodConstructor {
  private details: PathMethodDetails;
  private schema: ApiSchema;

  constructor(details: PathMethodDetails, schema: ApiSchema) {
    this.details = details;
    this.schema = schema;
  }

  construct(): ExcelFunctionParameter[] {
    if (!this.details.requestBody?.content[JSON_CONTENT_TYPE]?.schema.$ref) {
      return [];
    }

    const ref = this.details.requestBody.content[JSON_CONTENT_TYPE].schema.$ref;
    const requestBodySchema = this.getRequestBodySchema(ref);

    if (!requestBodySchema.properties) {
      return [];
    }

    return this.mapPropertiesToParameters(requestBodySchema.properties, requestBodySchema.required);
  }

  private getRequestBodySchema(ref: string): any {
    const schemaName = ref.split("/").pop() as string;
    return this.schema.components.schemas[schemaName];
  }

  private mapPropertiesToParameters(
    properties: Record<string, any>,
    required: string[] = []
  ): ExcelFunctionParameter[] {
    const parameters: ExcelFunctionParameter[] = [];

    for (const [name, details] of Object.entries(properties)) {
      const startIndex = parameters.length;
      if (details.$ref) {
        this.handleRefProperty(name, details, parameters);
      } else if (details.type === "array" && details.items && details.items.$ref) {
        this.handleArrayWithRefItems(name, details, parameters);
      } else {
        this.handlePrimitiveProperty(name, details, parameters);
      }
      for (const param of parameters.slice(startIndex)) {
        param.optional = !required.includes(name) || param.optional === true;
      }
    }

    return parameters;
  }

  private handleRefProperty(name: string, details: any, parameters: ExcelFunctionParameter[]): void {
    const refSchema = this.getRequestBodySchema(details.$ref);
    if (!refSchema) return;

    if (refSchema.properties) {
      const nestedParams = this.mapPropertiesToParameters(refSchema.properties, refSchema.required);
      nestedParams.forEach((param) => {
        parameters.push({
          ...param,
          name: `${name}.${param.name}`,
          description: param.description,
        });
      });
    } else {
      const dimensionality = refSchema.type === "array" ? EXCEL_DIMENSIONALITY.matrix : EXCEL_DIMENSIONALITY.scalar;
      const { type, schemaType, nestLevel } = getType(refSchema);
      parameters.push({
        description: refSchema.title || details.title || name,
        name,
        default: details.default !== undefined ? details.default : refSchema.default,
        type,
        schemaType,
        dimensionality,
        nestLevel,
      });
    }
  }

  private handleArrayWithRefItems(name: string, details: any, parameters: ExcelFunctionParameter[]): void {
    const itemRefSchema = this.getRequestBodySchema(details.items.$ref);
    if (!itemRefSchema) return;

    if (itemRefSchema.properties) {
      const nestedParams = this.mapPropertiesToParameters(itemRefSchema.properties, itemRefSchema.required);
      nestedParams.forEach((param) => {
        parameters.push({
          ...param,
          name: `${name}[].${param.name}`,
          description: param.description,
          dimensionality: EXCEL_DIMENSIONALITY.matrix,
        });
      });
    } else {
      const { type, schemaType } = getType(itemRefSchema);
      parameters.push({
        description: itemRefSchema.title || details.title || name,
        name,
        type,
        schemaType,
        dimensionality: EXCEL_DIMENSIONALITY.matrix,
        nestLevel: 1,
      });
    }
  }

  private handlePrimitiveProperty(name: string, details: any, parameters: ExcelFunctionParameter[]): void {
    const dimensionality = details.type === "array" ? EXCEL_DIMENSIONALITY.matrix : EXCEL_DIMENSIONALITY.scalar;
    const { type, schemaType, nestLevel } = getType(details);

    parameters.push({
      description: details.title || name,
      name,
      default: details.default,
      type,
      schemaType,
      dimensionality,
      nestLevel,
    });
  }
}

export class OpenApiExcelFunctionConstructor {
  private schema: ApiSchema;
  private baseUrl: string;
  public notSupportedApiEndpoints: string[] = [];

  static ApiMethodConstructorStrategy = {
    [ApiMethod.GET]: GetMethodConstructor,
    [ApiMethod.POST]: PostMethodConstructor,
  };

  constructor(schema: ApiSchema, baseUrl: string) {
    this.schema = schema;
    this.baseUrl = baseUrl;
  }

  construct(): ExcelFunction[] {
    const functions: ExcelFunction[] = [];

    for (const [path, methods] of Object.entries(this.schema.paths)) {
      for (const [method, details] of Object.entries(methods)) {
        const functionName = this.getFunctionName(path);

        try {
          const parameters = this.getParameters(method as ApiMethod, details as PathMethodDetails);

          if (parameters.some((param) => param.type === "object")) {
            throw new Error(`Object type parameters not supported in ${functionName}`);
          }

          if (parameters.length >= 255) {
            throw new Error(`Too many parameters for function ${functionName}`);
          }

          functions.push({
            id: functionName,
            name: `${functionName}`,
            description: functionName,
            parameters,
            result: {
              type: "string",
            },
            url: constructUrl(this.baseUrl, path),
            method,
            options: {
              requiresAddress: true,
              requiresParameterAddresses: true,
            },
          });
        } catch (error) {
          this.notSupportedApiEndpoints.push(functionName);
          console.warn(`Error constructing parameters for method ${method}:`, error);
        }
      }
    }

    return functions;
  }

  private getFunctionName(path: string): string {
    const name = path
      .replace(/^\/|\/$/g, "") // Remove leading/trailing slashes
      .replace(/[/-]+/g, "_"); // Replace slashes and dashes with underscores

    // Excel function names must start with a letter (A-Z), prefix with 'func' if it starts with a number or underscore
    return /^[A-Za-z]/.test(name) ? name : `func${name}`;
  }

  private getParameters(method: ApiMethod, details: PathMethodDetails): ExcelFunctionParameter[] {
    const apiMethodConstructorStrategy = this.getApiMethodConstructorStrategy(method);
    const constructor = new apiMethodConstructorStrategy(details, this.schema);
    return constructor.construct().sort((a, b) => Number(a.optional === true) - Number(b.optional === true));
  }

  private getApiMethodConstructorStrategy(
    method: ApiMethod
  ): typeof GetMethodConstructor | typeof PostMethodConstructor {
    const methodConstructorStrategy =
      OpenApiExcelFunctionConstructor.ApiMethodConstructorStrategy[
        method as keyof typeof OpenApiExcelFunctionConstructor.ApiMethodConstructorStrategy
      ];

    if (!methodConstructorStrategy) {
      throw new Error(`Method ${method} is not supported`);
    }

    return methodConstructorStrategy;
  }
}
